import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AuthContext, type AuthContextValue } from './AuthContext.ts';
import { fetchCurrentUser, signIn as apiSignIn, signOut as apiSignOut } from './authApi.ts';
import { readLastKnownUser } from './lastKnownUser.ts';
import { getTokens, subscribe } from './tokenStore.ts';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasTokens, setHasTokens] = useState(() => getTokens() !== null);

  // The token store is plain module state so that the fetch wrapper can reach it
  // without a React dependency. This subscription keeps the tree in step with it.
  useEffect(() => subscribe((tokens) => setHasTokens(tokens !== null)), []);

  const { data, isPending } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    enabled: hasTokens,
    staleTime: 5 * 60 * 1000,
    retry: false,
    // Start from the account this device last saw, so an offline reload renders the list
    // instead of a spinner or the sign-in screen. It is a *starting point*, not a cache:
    // the query is asked again immediately (see initialDataUpdatedAt), and if the server
    // is reachable and has refused the session, fetchCurrentUser forgets both the tokens
    // and this. If it is unreachable, the failure leaves this in `data` and the app
    // carries on with a user id it has every reason to still believe in.
    //
    // Passed as a function so it is read afresh: signing out clears it, and the next
    // person to sign in on this phone must not inherit it.
    initialData: () => readLastKnownUser() ?? undefined,
    // Zero, so `staleTime` does not apply to it. Without this the remembered user would
    // be treated as five minutes fresh and a session revoked overnight would be believed
    // until something else happened to refetch.
    initialDataUpdatedAt: 0,
  });

  const signIn = useCallback(
    async (email: string, password: string) => {
      await apiSignIn(email, password);
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    [queryClient],
  );

  const signOut = useCallback(() => {
    apiSignOut();
    // Everything cached belongs to the person who just left.
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data ?? null,
      isLoading: hasTokens && isPending,
      isAuthenticated: hasTokens && data !== undefined,
      signIn,
      signOut,
    }),
    [data, hasTokens, isPending, signIn, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
